// Pure view logic for the admin Users page: payment-tier derivation plus the
// per-column filter and sort semantics. Browser-free so the unit tier covers it.

export interface AdminUserEntry {
  id: string;
  email: string;
  full_name: string | null;
  role: 'user' | 'admin';
  created_at: string;
  last_active_at: string | null;
  deleted_at: string | null;
  is_banned: boolean;
  subscription_status: string | null;
  mfa_enrolled: boolean;
  firm_admin: boolean;
  has_seat: boolean;
  firm_name: string | null;
  projects_created: number;
  projects_completed: number;
  /**
   * Mailing state. The three opt-outs are separate lists on purpose — quitting
   * the newsletter or an announcement must not switch off the check-in nudges.
   * See netlify/lib/mailingLists.js, which is where the lists are defined.
   */
  email_optout_at: string | null;
  newsletter_optout_at: string | null;
  announcement_optout_at: string | null;
  /**
   * Keeps an account out of the outreach digest. It does NOT block sending —
   * a test account is exactly what you want to send to when checking that
   * sending works — so it only excludes the account from Select all.
   */
  excluded_from_digest: boolean;
}

/** Payment tier shown in the Status column for non-admin accounts. */
export type PaymentTier = 'ent_admin' | 'ent' | 'subscribed' | 'ppu';

export const TIER_LABELS: Record<PaymentTier, string> = {
  ent_admin: 'Ent (admin)',
  ent: 'Ent',
  subscribed: 'Subscribed',
  ppu: 'PPU',
};

export function getPaymentTier(
  u: Pick<AdminUserEntry, 'firm_admin' | 'has_seat' | 'subscription_status'>
): PaymentTier {
  // Firm capabilities are independent: admin wins the badge whether or not
  // they also hold a seat; a seat alone is a plain enterprise member.
  if (u.firm_admin) return 'ent_admin';
  if (u.has_seat) return 'ent';
  // Matches the paywall: only a live Stripe subscription counts as subscribed.
  if (u.subscription_status === 'active') return 'subscribed';
  return 'ppu';
}

/** Account state overlaid on the tier badge; null means the account is normal. */
export type AccountState = 'deleted' | 'locked' | null;

export function getAccountState(
  u: Pick<AdminUserEntry, 'deleted_at' | 'is_banned'>
): AccountState {
  if (u.deleted_at) return 'deleted';
  if (u.is_banned) return 'locked';
  return null;
}

// ─── Filtering ───────────────────────────────────────────────────────────────

export type StatusFilter = 'all' | 'admin' | PaymentTier | 'locked' | 'deleted';

export interface UserFilters {
  /** Case-insensitive substring match on name or email. */
  search: string;
  status: StatusFilter;
  /** yyyy-mm-dd bounds from <input type="date">; '' = unset. */
  joinedFrom: string;
  joinedTo: string;
  activeFrom: string;
  activeTo: string;
  /** Raw numeric input values; '' or non-numeric = unset. */
  minCreated: string;
  minCompleted: string;
}

export const EMPTY_FILTERS: UserFilters = {
  search: '',
  status: 'all',
  joinedFrom: '',
  joinedTo: '',
  activeFrom: '',
  activeTo: '',
  minCreated: '',
  minCompleted: '',
};

export function hasActiveFilters(f: UserFilters): boolean {
  return (
    f.search.trim() !== '' ||
    f.status !== 'all' ||
    f.joinedFrom !== '' ||
    f.joinedTo !== '' ||
    f.activeFrom !== '' ||
    f.activeTo !== '' ||
    f.minCreated !== '' ||
    f.minCompleted !== ''
  );
}

/**
 * Local-timezone yyyy-mm-dd key, so date-range filters agree with the dates
 * the table renders via toLocaleDateString rather than with UTC.
 */
export function localDateKey(ts: string): string {
  const d = new Date(ts);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function inDateRange(ts: string | null, from: string, to: string): boolean {
  if (!from && !to) return true;
  if (!ts) return false;
  const key = localDateKey(ts);
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

function matchesStatus(u: AdminUserEntry, status: StatusFilter): boolean {
  switch (status) {
    case 'all':
      return true;
    case 'admin':
      return u.role === 'admin';
    case 'deleted':
      return u.deleted_at != null;
    case 'locked':
      return getAccountState(u) === 'locked';
    default:
      // Tier filters exclude admins — their badge shows Admin, not a tier.
      return u.role !== 'admin' && getPaymentTier(u) === status;
  }
}

function meetsMin(count: number, raw: string): boolean {
  const n = parseInt(raw, 10);
  return Number.isNaN(n) || count >= n;
}

export function filterUsers(users: AdminUserEntry[], f: UserFilters): AdminUserEntry[] {
  const q = f.search.trim().toLowerCase();
  return users.filter(
    u =>
      (!q ||
        (u.full_name ?? '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)) &&
      matchesStatus(u, f.status) &&
      inDateRange(u.created_at, f.joinedFrom, f.joinedTo) &&
      inDateRange(u.last_active_at, f.activeFrom, f.activeTo) &&
      meetsMin(u.projects_created, f.minCreated) &&
      meetsMin(u.projects_completed, f.minCompleted)
  );
}

// ─── Sorting ─────────────────────────────────────────────────────────────────

export type UserSortKey =
  | 'name'
  | 'status'
  | 'created_at'
  | 'last_active_at'
  | 'projects_created'
  | 'projects_completed';

/** Direction applied on the first click of each column header. */
export const DEFAULT_SORT_ASC: Record<UserSortKey, boolean> = {
  name: true, // A→Z
  status: true, // admin → enterprise → subscribed → PPU → locked → deleted
  created_at: false, // newest first
  last_active_at: false, // most recent first
  projects_created: false, // highest first
  projects_completed: false,
};

const TIER_RANK: Record<PaymentTier, number> = {
  ent_admin: 1,
  ent: 2,
  subscribed: 3,
  ppu: 4,
};

function statusRank(u: AdminUserEntry): number {
  if (u.role === 'admin') return 0;
  const state = getAccountState(u);
  if (state === 'deleted') return 6;
  if (state === 'locked') return 5;
  return TIER_RANK[getPaymentTier(u)];
}

export function sortUsers(
  users: AdminUserEntry[],
  key: UserSortKey,
  asc: boolean
): AdminUserEntry[] {
  const dir = asc ? 1 : -1;
  return [...users].sort((a, b) => {
    switch (key) {
      case 'name': {
        const an = (a.full_name || a.email).toLowerCase();
        const bn = (b.full_name || b.email).toLowerCase();
        return dir * an.localeCompare(bn);
      }
      case 'status':
        return (
          dir * (statusRank(a) - statusRank(b)) ||
          a.email.localeCompare(b.email)
        );
      case 'created_at':
        return (
          dir *
          (new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        );
      case 'last_active_at': {
        // Users with no recorded activity sort last in either direction.
        if (!a.last_active_at && !b.last_active_at) return 0;
        if (!a.last_active_at) return 1;
        if (!b.last_active_at) return -1;
        return (
          dir *
          (new Date(a.last_active_at).getTime() -
            new Date(b.last_active_at).getTime())
        );
      }
      case 'projects_created':
        return dir * (a.projects_created - b.projects_created);
      case 'projects_completed':
        return dir * (a.projects_completed - b.projects_completed);
    }
  });
}
